// Greenhold component prototype: <TierBadge>. Story 3.5.
//
// Mounted at /prototype/tier-badge (no guard) per UX spec L444 — the
// team's MVP-Storybook substitute. Shows the canonical tier-badge
// primitive in 4 visual-review sections:
//
//   1. 4 tiers × 3 sizes — text-only (12 badges).
//   2. 4 tiers × 3 sizes — with icon prefix (12 badges).
//   3. In-context (vault-grid-row mock — avatar + name + badge + edition).
//   4. className composition demo (consumer overrides via tailwind-merge).
//
// Verification surface for the Story 3.5 swap closures: navigate also
// to /prototype/certificate (Certificate post-swap regression) + / home
// (App.tsx homepage post-swap regression) + /prototype/dossier-shop-card
// (DossierShopCard tier-mix dots non-swap sanity).

import {
  TierBadge,
  type TierBadgeTier,
  type TierBadgeSize,
} from "@greenhold/components";

const TIERS: ReadonlyArray<TierBadgeTier> = [
  "common",
  "preferred",
  "premium",
  "founders",
];
const SIZES: ReadonlyArray<TierBadgeSize> = ["sm", "md", "lg"];

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <h2 className="text-sm uppercase tracking-wider font-mono opacity-70 mb-3">
      {children}
    </h2>
  );
}

function SizeRow({
  size,
  withIcon,
}: {
  size: TierBadgeSize;
  withIcon: boolean;
}) {
  return (
    <div className="flex items-center gap-4">
      <span className="w-12 text-xs font-mono opacity-60">{size}</span>
      <div className="flex flex-wrap items-center gap-3">
        {TIERS.map((tier) => (
          <TierBadge key={tier} tier={tier} size={size} withIcon={withIcon} />
        ))}
      </div>
    </div>
  );
}

// Vault-grid-row mock — avatar circle + creator name + tier badge +
// edition number. Demonstrates the badge anchoring in a real
// composition context without dragging in actual Story 4.x components.
function VaultRowMock({
  tier,
  creator,
  edition,
}: {
  tier: TierBadgeTier;
  creator: string;
  edition: number;
}) {
  return (
    <div className="flex items-center gap-3 p-3 rounded-md border border-brand-forest-100 bg-brand-paper-50">
      <div className="w-10 h-10 rounded-full bg-brand-forest-900 text-brand-paper-100 flex items-center justify-center font-heading font-bold">
        {creator[0]}
      </div>
      <span className="flex-1 font-heading font-semibold">{creator}</span>
      <TierBadge tier={tier} size="sm" />
      <span className="font-mono text-xs opacity-70">
        EDITION {String(edition).padStart(4, "0")}
      </span>
    </div>
  );
}

export function TierBadgePrototype() {
  return (
    <main className="min-h-screen bg-brand-paper-100 text-brand-forest-900 p-8">
      <div className="max-w-6xl mx-auto space-y-12">
        <header>
          <h1 className="text-3xl font-heading font-bold mb-2">
            &lt;TierBadge&gt; prototype
          </h1>
          <p className="opacity-80">
            Story 3.5 — the canonical tier-badge primitive. Wraps shadcn
            Badge (variant="outline"); 4 tier variants × 3 sizes × optional
            icon prefix (UX-DR13 + UX-DR47). Consumed by Story 3.3
            Certificate (post-swap) + Story 1.3 homepage smoke-test (post-
            swap) + Story 4.x vault grid + Story 5.x marketplace tiles.
          </p>
        </header>

        {/* Section 1 — 4 tiers × 3 sizes, text-only. */}
        <section>
          <SectionLabel>
            Section 1 — 4 tiers × 3 sizes (text-only)
          </SectionLabel>
          <div className="space-y-4">
            {SIZES.map((size) => (
              <SizeRow key={size} size={size} withIcon={false} />
            ))}
          </div>
        </section>

        {/* Section 2 — same matrix, with tier-shape icon prefix. */}
        <section>
          <SectionLabel>
            Section 2 — 4 tiers × 3 sizes (with tier-shape icon prefix
            for color-blind safety: square / diamond / hexagon / star)
          </SectionLabel>
          <div className="space-y-4">
            {SIZES.map((size) => (
              <SizeRow key={size} size={size} withIcon={true} />
            ))}
          </div>
        </section>

        {/* Section 3 — in-context (vault-grid-row mock). */}
        <section>
          <SectionLabel>
            Section 3 — in-context (vault-grid-row mock)
          </SectionLabel>
          <div className="space-y-2 max-w-xl">
            <VaultRowMock tier="common" creator="Marie" edition={87} />
            <VaultRowMock tier="preferred" creator="Sjokz" edition={42} />
            <VaultRowMock tier="premium" creator="Caps" edition={1337} />
            <VaultRowMock tier="founders" creator="Faker" edition={1} />
          </div>
        </section>

        {/* Section 4 — className composition demo. */}
        <section>
          <SectionLabel>
            Section 4 — className composition (consumer overrides win via
            tailwind-merge)
          </SectionLabel>
          <div className="flex items-center gap-4">
            <span className="text-xs font-mono opacity-60">
              rotate-12 shadow-lg
            </span>
            <TierBadge
              tier="founders"
              size="lg"
              withIcon
              className="rotate-12 shadow-lg"
            />
          </div>
        </section>
      </div>
    </main>
  );
}
